/**
 * A generic handler for debouncing a function call.
 * Debouncing prevents extra activations or slow functions from triggering too often.
 * @param func function to be debounced
 * @param wait debounce wait time in milliseconds
 */
export function debounce(func, wait) {
    let timeout = null;
    function debounced(...args) {
        if (timeout !== null) {
            clearTimeout(timeout);
        }
        timeout = setTimeout(() => {
            func.apply(this, args);
            timeout = null;
        }, wait);
    }
    debounced.cancel = () => {
        if (timeout !== null) {
            clearTimeout(timeout);
            timeout = null;
        }
    };
    return debounced;
}
/**
 * A method that throttles the execution of a function, ensuring that it is called at most once within a specified time limit.
 * Throttle differs from debounce in that the function is invoked immediately on the first call, whereas debounce is delayed on the first call.
 *
 * @param func - The function to be throttled.
 * @param limit - The time limit (in milliseconds) within which the function should be called at most once.
 * @returns A throttled version of the input function that can be called with the same arguments.
 */
export function throttle(func, limit) {
    let inThrottle = false;
    return function (...args) {
        if (!inThrottle) {
            func.apply(this, args);
            inThrottle = true;
            setTimeout(() => (inThrottle = false), limit);
        }
    };
}
/**
 * Combination of debounce and throttle. Runs on the leading edge, but also runs at the end of the timer if called multiple times during the time limit
 *
 * @param func - The function to throttle.
 * @param limit - The time limit (in milliseconds) between consecutive executions of the function.
 * @returns A throttled function that will execute `func` after the specified limit has elapsed since the last execution.
 */
export function trailingThrottle(func, limit) {
    let lastFunc;
    let lastRan = null;
    return function (...args) {
        if (!lastRan) {
            func.apply(this, args);
            lastRan = Date.now();
        }
        else {
            clearTimeout(lastFunc);
            lastFunc = setTimeout(() => {
                if ((Date.now() - lastRan) >= limit) {
                    func.apply(this, args);
                    lastRan = Date.now();
                }
            }, limit - (Date.now() - lastRan));
        }
    };
}
